// Phase 6 — Track E T-E3. Button primitive.
//
// Hand-adapted from shadcn/ui's Button to consume DailyDeck tokens via
// Tailwind classes that resolve to var(--color-…) at runtime. Variants:
//
//   * primary    — accent fill, on-accent label. The only "loud" CTA.
//   * secondary  — surface-raised fill, default border, primary text.
//   * ghost      — transparent fill, primary text, hover surface-raised.
//   * destructive— state-danger fill, white label. Reserved for irrecoverable actions.
//   * link       — primary text underlined, no chrome.
//
// Sizes mirror blueprint §14.7 — `default` (40px tall), `sm` (32px), `lg` (48px),
// and `icon` (square — used by IconButton).

import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn.js';

const buttonVariants = cva(
  // base
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-body-strong transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-default focus-visible:ring-offset-2 focus-visible:ring-offset-surface-base disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary:
          'bg-accent-default text-text-on-accent hover:bg-accent-hover active:bg-accent-pressed',
        secondary:
          'bg-surface-raised text-text-primary border border-border-default hover:bg-accent-subtle',
        ghost:
          'bg-transparent text-text-primary hover:bg-surface-raised',
        // D7-fix Fix-3: dark-mode `state-danger` (#E36A6F) on `#FFFFFF` only
        // hit 3.2:1, failing axe `color-contrast`. Switch the destructive
        // foreground to the theme-aware `text-on-accent` token (#FFFFFF in
        // light, #0E0F12 in dark) so both palettes clear AA-normal — light
        // 6.77:1, dark 5.35:1. The pairing is locked into the contrast
        // script via `text-on-accent on state-danger` so a future token
        // retune that breaks either consumer surfaces immediately.
        destructive:
          'bg-state-danger text-text-on-accent hover:opacity-90',
        link:
          'bg-transparent text-accent-default underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-8 px-3 text-caption',
        lg: 'h-12 px-6',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
